import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { flagEmoji, geoName } from "@/lib/format";

// Compact per-GEO personal override editor shared by Offer → Users and User → Offer Rates.
// Empty input = Inherit (falls through: user default → offer GEO rate → offer default).
// value map shape: { GEO: { affiliate_payout | rs_affiliate_percent | advertiser_payout } }
export function GeoOverridesDialog({ open, onClose, geos, value, onApply, percent, staff, dynamicRevenue, geoRates, defaults }) {
  const [draft, setDraft] = useState({});
  useEffect(() => { if (open) setDraft(JSON.parse(JSON.stringify(value || {}))); }, [open, value]);
  const payField = percent ? "rs_affiliate_percent" : "affiliate_payout";
  const get = (g, f) => { const v = draft?.[g]?.[f]; return v == null ? "" : v; };
  const upd = (g, f, v) => setDraft((d) => {
    const e = { ...(d[g] || {}) };
    if (v === "" || v == null) delete e[f]; else e[f] = v;
    const next = { ...d };
    if (Object.keys(e).length) next[g] = e; else delete next[g];
    return next;
  });
  const offerGeoVal = (g, f) => {
    const v = geoRates?.[g]?.[f];
    return v == null ? null : v;
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/[0.1] bg-[#0A0A0A]" data-testid="geo-overrides-dialog">
        <DialogTitle className="text-sm font-medium text-foreground">Personal GEO rates</DialogTitle>
        <p className="text-xs text-muted-foreground">Empty = Inherit (user default override → offer GEO rate → offer default). 0 is an explicit value.</p>
        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
          {(geos || []).map((g) => (
            <div key={g} className="flex flex-wrap items-center gap-2" data-testid={`geo-ovr-row-${g}`}>
              <span className="w-28 shrink-0 truncate text-sm text-foreground" title={geoName(g)}>{flagEmoji(g)} {g}</span>
              {staff && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">Rev</span>
                  {dynamicRevenue
                    ? <span className="w-20 text-center text-[11px] font-medium text-[#F59E0B]">Dynamic</span>
                    : <input type="number" value={get(g, "advertiser_payout")} onChange={(e) => upd(g, "advertiser_payout", e.target.value)}
                        placeholder={offerGeoVal(g, "advertiser_payout") ?? defaults?.revenue ?? "Inherit"} data-testid={`geo-ovr-rev-${g}`}
                        className="h-8 w-20 rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2 text-right font-mono text-xs text-foreground outline-none focus:border-white/30" />}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">{percent ? "Payout %" : "Payout"}</span>
                <input type="number" value={get(g, payField)} onChange={(e) => upd(g, payField, e.target.value)}
                  placeholder={offerGeoVal(g, "affiliate_payout") ?? defaults?.payout ?? "Inherit"} data-testid={`geo-ovr-pay-${g}`}
                  className="h-8 w-20 rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2 text-right font-mono text-xs text-foreground outline-none focus:border-white/30" />
              </div>
              {!draft?.[g] && <span className="text-[10px] text-muted-foreground/60">Inherit</span>}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/[0.12]" data-testid="geo-ovr-cancel">Cancel</Button>
          <Button onClick={() => { onApply(draft); onClose(); }} className="bg-white text-black hover:bg-white/90" data-testid="geo-ovr-apply">Apply</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Compact GEO badges: flags + codes, truncated with "+N" (full list in the title tooltip).
export function GeoBadges({ geo, geos, max = 3, testId }) {
  const list = (Array.isArray(geos) && geos.length ? geos : String(geo || "").split(",").map((x) => x.trim()).filter(Boolean));
  if (list.length <= 1) return <span data-testid={testId}>{list[0] ? `${flagEmoji(list[0])} ${geoName(list[0])}` : "—"}</span>;
  const shown = list.slice(0, max);
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={testId} title={list.map((g) => `${g} — ${geoName(g)}`).join("\n")}>
      {shown.map((g) => (
        <span key={g} className="inline-flex items-center gap-0.5 rounded border border-white/10 bg-white/[0.04] px-1 py-0.5 text-[11px]">{flagEmoji(g)} {g}</span>
      ))}
      {list.length > max && <span className="rounded border border-white/10 bg-white/[0.06] px-1 py-0.5 text-[11px] text-muted-foreground">+{list.length - max}</span>}
    </span>
  );
}
